import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, Check, ChevronDown, ChevronRight, ChevronUp, Edit2, Film, FolderOpen, LayoutGrid, List, Loader, Lock, Play, Plus, RefreshCw, Settings, SkipForward, Star, Trash2, User, UserCheck, X } from 'lucide-react';
import { Icon } from './Icon';
import { contrastText } from '../constants/theme';
import ClearableField from './ClearableField';
import LoadFailure from './LoadFailure';
import PageHelp from './PageHelp';
import {
  DASHBOARD_BUCKETS,
  DEFAULT_KANBAN_COLUMNS,
  PIPELINE_STAGES_FE,
  PIPELINE_STATUS_META,
  SORT_OPTIONS,
  STAGE_COLORS,
  getKanbanColumnForStage,
} from '../constants/pipeline';
import { getLanguageCode, getLanguageMeta } from '../constants/languages';
import { Z } from '../constants/tokens';
import { getPartBucket } from '../utils/pipelineUtils';
import Badge from './ui/Badge';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';

// Whether this device has a real hovering pointer.
//
// Touch devices never fire mouseenter, so anything rendered only under a hover
// flag simply does not exist there. The pipeline gear was gated that way, and
// it is the sole trigger for the assignee / skip / reset popover — which made
// that whole workflow unreachable on iPad, a supported device.
//
// Evaluated once at module load: a device does not grow a mouse mid-session,
// and re-checking per render would cost more than it is worth.
const CAN_HOVER =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(hover: hover)').matches
    : true;

export default function DashboardPanel({ axiosAuth, currentUser, theme, isDarkMode, onOpenInterview, onOpenProjectKanban, onOpenDirectory, workingDirLabel, defaultProject, onSetDefaultProject }) {
  const isAdmin = currentUser?.role === "admin";
  const welcomeName = useMemo(() => {
    const raw = currentUser?.display_name || currentUser?.email?.split('@')[0] || '';
    return String(raw)
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }, [currentUser]);

  const [projects,       setProjects]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [usersSummary,   setUsersSummary]   = useState([]);
  const [allUsers,       setAllUsers]       = useState([]);
  const [assignFilter,   setAssignFilter]   = useState("assigned"); // 'assigned' | 'unassigned'
  const [userFilterId,   setUserFilterId]   = useState(null);
  const [expandedRoot,   setExpandedRoot]   = useState(null);
  const [saving,         setSaving]         = useState({});
  const [sortKey,        setSortKey]        = useState("name");
  const [sortDir,        setSortDir]        = useState("asc");
  // Language editor state — { root, value } or null
  const [langEdit,       setLangEdit]       = useState(null);
  const [langInput,      setLangInput]      = useState("");
  const [knownLanguages, setKnownLanguages] = useState([]);
  // True while the admin has chosen "Other…" and is typing a language that is
  // not in knownLanguages. Kept in the parent (like langEdit/langInput) so it
  // survives LangBadge re-rendering.
  const [langOther,      setLangOther]      = useState(false);
  // Team Workload sort: name | access (role) | projects | tasks
  const [workloadSort,   setWorkloadSort]   = useState(() => localStorage.getItem('gwv_workload_sort') || 'name');
  useEffect(() => { localStorage.setItem('gwv_workload_sort', workloadSort); }, [workloadSort]);
  // Kanban board state
  const [viewMode,       setViewMode]       = useState(() => localStorage.getItem('gwv_dashboard_view') || 'list'); // 'list' | 'board'
  const [kanbanColumns,  setKanbanColumns]  = useState(() => {
    try { const s = localStorage.getItem('gwv_kanban_columns'); return s ? JSON.parse(s) : DEFAULT_KANBAN_COLUMNS; }
    catch { return DEFAULT_KANBAN_COLUMNS; }
  });
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  // Recalculate pipeline state
  const [recalcState,    setRecalcState]    = useState(null); // null | 'running' | { updated_rows, projects_touched }
  // Task assignment modal: { userId, userName, projectRoot, parts } or null
  const [taskModal,      setTaskModal]      = useState(null);
  const [taskRows,       setTaskRows]       = useState([]); // [{id, part_number, tc_start, tc_end, note}]
  // A failed load used to render as "Full access to all tapes (no
  // restrictions)" — asserting a permission state that may be untrue.
  const [taskRowsError,  setTaskRowsError]  = useState(false);
  const [taskSaving,     setTaskSaving]     = useState(false);

  const runRecalculate = () => {
    setRecalcState('running');
    axiosAuth.post('/pipeline/recalculate')
      .then(r => {
        setRecalcState(r.data);
        loadAll();   // refresh dashboard after recalc
      })
      .catch(() => setRecalcState({ error: true }));
  };

  const loadAll = useCallback(() => {
    setLoading(true);
    axiosAuth.get("/pipeline/projects")
      .then(r => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    if (isAdmin) {
      axiosAuth.get("/pipeline/users-summary").then(r => setUsersSummary(r.data)).catch(() => {});
      axiosAuth.get("/admin/users").then(r => setAllUsers((r.data || []).filter(u => u.is_active !== false))).catch(() => {});
      axiosAuth.get("/pipeline/languages").then(r => setKnownLanguages(r.data?.all || [])).catch(() => {});
    }
  }, [axiosAuth, isAdmin]);

  // Silent background refresh — does NOT set loading=true, so the UI doesn't flicker/collapse
  const silentRefresh = useCallback(() => {
    axiosAuth.get("/pipeline/projects").then(r => setProjects(r.data)).catch(() => {});
    if (isAdmin) {
      axiosAuth.get("/pipeline/users-summary").then(r => setUsersSummary(r.data)).catch(() => {});
    }
  }, [axiosAuth, isAdmin]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Persist kanban view preferences
  useEffect(() => { localStorage.setItem('gwv_dashboard_view', viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem('gwv_kanban_columns', JSON.stringify(kanbanColumns)); }, [kanbanColumns]);

  const updateStage = (projectRoot, partNumber, stage, patch) => {
    const key = `${projectRoot}_${partNumber}_${stage}`;
    setSaving(s => ({ ...s, [key]: true }));
    axiosAuth.put("/pipeline/stage", { project_root: projectRoot, part_number: partNumber, stage, ...patch })
      .then(silentRefresh).catch(() => {})
      .finally(() => setSaving(s => { const n = { ...s }; delete n[key]; return n; }));
  };

  const skipStage = (projectRoot, partNumber, stage) =>
    axiosAuth.put("/pipeline/stage/skip", { project_root: projectRoot, part_number: partNumber, stage })
      .then(silentRefresh).catch(() => {});

  // Open the language editor. A value that is not in knownLanguages opens
  // straight into free-text ("Other…") mode, so an existing unusual language
  // stays visible and editable instead of the select blanking it out.
  const openLangEdit = (projectRoot, value = '') => {
    const v = value || '';
    setLangEdit(projectRoot);
    setLangInput(v);
    setLangOther(!!v && !knownLanguages.includes(v));
  };

  const confirmLanguage = (projectRoot, language, confirmed = true) => {
    axiosAuth.put("/pipeline/language", { project_root: projectRoot, language, confirmed })
      .then(() => { setLangEdit(null); setLangOther(false); silentRefresh(); }).catch(() => {});
  };

  // ── Assign counts ──
  const assignCounts = useMemo(() => {
    return {
      assigned:   projects.filter(p => p.assigned_names?.length > 0).length,
      unassigned: projects.filter(p => !p.assigned_names?.length).length,
    };
  }, [projects]);

  // ── Filter + sort ──
  const displayedProjects = useMemo(() => {
    let list = projects;
    // Assignment filter
    if (assignFilter === 'assigned')   list = list.filter(p => p.assigned_names?.length > 0);
    if (assignFilter === 'unassigned') list = list.filter(p => !p.assigned_names?.length);
    if (userFilterId != null) {
      list = list.filter(p => (p.assigned_ids || []).includes(userFilterId));
    }
    list = [...list].sort((a, b) => {
      // Favorite project always first
      const aFav = defaultProject && a.project_root === defaultProject ? 1 : 0;
      const bFav = defaultProject && b.project_root === defaultProject ? 1 : 0;
      if (aFav !== bFav) return bFav - aFav;
      let va, vb;
      if (userFilterId != null || sortKey === "name") {
        va = a.project_root.toLowerCase(); vb = b.project_root.toLowerCase();
      } else if (sortKey === "stage") {
        va = a.current_stage; vb = b.current_stage;
      } else if (sortKey === "pct") {
        va = a.progress_pct; vb = b.progress_pct;
      } else if (sortKey === "language") {
        va = (a.interview_language || "zzz").toLowerCase();
        vb = (b.interview_language || "zzz").toLowerCase();
      } else if (sortKey === "updated") {
        va = a.last_updated || ""; vb = b.last_updated || "";
      } else if (sortKey === "assignee") {
        va = (a.assigned_names?.[0] || "zzz").toLowerCase();
        vb = (b.assigned_names?.[0] || "zzz").toLowerCase();
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [projects, assignFilter, sortKey, sortDir, userFilterId, defaultProject]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const inputSm = {
    padding: '4px 8px', borderRadius: '6px', border: `1px solid ${theme.border}`,
    background: theme.inputBg, color: theme.text, fontSize: '0.72rem', outline: 'none', width: '100%',
  };

  // ── Language badge ──
  const LangBadge = ({ proj, compact = false }) => {
    const confirmed  = proj.language_confirmed;
    const lang       = proj.interview_language;
    const isEditing  = langEdit === proj.project_root;
    const langMeta   = getLanguageMeta(lang);
    const badgeStyle = {
      padding: compact ? '3px 9px' : '4px 10px',
      borderRadius: '999px',
      fontSize: compact ? '0.64rem' : '0.62rem',
      fontWeight: 700,
      background: confirmed ? langMeta.bg : 'rgba(245,158,11,0.10)',
      color: confirmed ? langMeta.text : '#f59e0b',
      border: `1px solid ${confirmed ? langMeta.border : 'rgba(245,158,11,0.3)'}`,
      minHeight: compact ? '24px' : '22px',
      minWidth: compact ? '86px' : '92px',
      justifyContent: 'center',
      display: 'inline-flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
    };

    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} onClick={e => e.stopPropagation()}>
          {/* Native <select> rather than a <datalist>: Safari on iPad never
              renders datalist suggestions, which left admins a blank free-text
              box with no idea which languages exist. "Other…" keeps the ability
              to enter a language that is not in the list yet. */}
          {!langOther ? (
            <select
              id={`gwv-langs-${proj.project_root}`}
              value={knownLanguages.includes(langInput) ? langInput : ''}
              onChange={e => {
                if (e.target.value === '__other__') { setLangOther(true); setLangInput(''); }
                else setLangInput(e.target.value);
              }}
              aria-label="Interview language"
              style={{ ...inputSm, width: compact ? '108px' : '110px', padding: compact ? '3px 8px' : '3px 7px', height: compact ? '24px' : 'auto' }}
              autoFocus
            >
              <option value="">Language…</option>
              {[...knownLanguages].sort((a, b) => a.localeCompare(b)).map(l => (
                <option key={l} value={l}>{l}</option>
              ))}
              <option value="__other__">Other…</option>
            </select>
          ) : (
            <ClearableField
              theme={theme}
              value={langInput}
              onChange={e => setLangInput(e.target.value)}
              onClear={() => { setLangInput(''); setLangOther(false); }}
              placeholder="New language…"
              autoComplete="off"
              aria-label="New interview language"
              style={{ ...inputSm, width: compact ? '108px' : '110px', padding: compact ? '3px 8px' : '3px 7px', height: compact ? '24px' : 'auto' }}
              autoFocus
            />
          )}
          <button onClick={() => confirmLanguage(proj.project_root, langInput.trim(), true)}
            disabled={!langInput.trim()}
            style={{ padding: compact ? '3px 8px' : '2px 8px', borderRadius: '7px', background: langInput.trim() ? '#22c55e' : theme.card, color: langInput.trim() ? '#fff' : theme.textSub, border: langInput.trim() ? 'none' : `1px solid ${theme.border}`, fontSize: compact ? '0.64rem' : '0.65rem', fontWeight: 700, cursor: langInput.trim() ? 'pointer' : 'not-allowed', height: compact ? '24px' : 'auto' }}>
            Confirm
          </button>
          <button onClick={e => { e.stopPropagation(); setLangEdit(null); setLangOther(false); }}
            style={{ padding: compact ? '3px 8px' : '2px 6px', borderRadius: '7px', background: 'transparent', color: theme.textSub, border: `1px solid ${theme.border}`, fontSize: compact ? '0.64rem' : '0.65rem', cursor: 'pointer', height: compact ? '24px' : 'auto' }}>
            Cancel
          </button>
        </div>
      );
    }

    if (!lang) {
      return isAdmin ? (
        <button onClick={e => { e.stopPropagation(); openLangEdit(proj.project_root, ''); }}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: compact ? '3px 8px' : '2px 8px', borderRadius: '999px', border: `1px dashed #f59e0b`, background: 'rgba(245,158,11,0.08)', color: '#f59e0b', fontSize: compact ? '0.64rem' : '0.62rem', cursor: 'pointer', fontWeight: 600, minHeight: compact ? '24px' : '22px', minWidth: compact ? '86px' : '92px', boxSizing: 'border-box' }}>
          <Icon icon={AlertTriangle} size={9} /> Set language
        </button>
      ) : (
        <span style={{ ...badgeStyle, color: theme.textSub, background: theme.bg, border: `1px solid ${theme.border}` }}>Unknown</span>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={badgeStyle}>
          {lang}
        </span>
        {!confirmed && (
          <span title="Language auto-detected — admin must confirm" style={{ fontSize: '0.58rem', color: '#f59e0b' }}>?</span>
        )}
        {isAdmin && (
          <button onClick={e => { e.stopPropagation(); openLangEdit(proj.project_root, lang); }}
            title="Edit language" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: compact ? '2px' : '1px 3px', color: theme.textSub, fontSize: compact ? '0.64rem' : '0.62rem', opacity: 0.6 }}>
            <Icon icon={Edit2} size={9} />
          </button>
        )}
        {!confirmed && isAdmin && (
          <button onClick={e => { e.stopPropagation(); confirmLanguage(proj.project_root, lang, true); }}
            style={{ padding: compact ? '3px 8px' : '1px 7px', borderRadius: '8px', background: 'transparent', border: `1px solid #22c55e`, color: '#22c55e', fontSize: compact ? '0.62rem' : '0.6rem', cursor: 'pointer', fontWeight: 600, height: compact ? '24px' : 'auto' }}>
            Confirm
          </button>
        )}
      </div>
    );
  };

  const RoleBadge = ({ role, compact = false }) => (
    <Badge theme={theme} role={role || 'viewer'} size={compact ? 'sm' : 'md'}>
      {role || 'viewer'}
    </Badge>
  );

  const CompletionDonut = ({ pct, width = 86 }) => {
    const size = 68;
    const stroke = 8;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const progress = Math.max(0, Math.min(100, pct || 0));
    const dashOffset = circumference * (1 - (progress / 100));
    const gradId = `donut-grad-${Math.random().toString(36).slice(2, 8)}`;

    return (
      <div style={{ width, minWidth: width, background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', boxSizing: 'border-box' }}>
        <div style={{ width: size + 8, height: size + 8, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={size + 8} height={size + 8} viewBox={`0 0 ${size + 8} ${size + 8}`} style={{ display: 'block' }}>
            <defs>
              <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#22c55e" />
                <stop offset="100%" stopColor={isDarkMode ? 'rgba(187,247,208,0.35)' : '#dcfce7'} />
              </linearGradient>
            </defs>
            <circle
              cx={(size + 8) / 2}
              cy={(size + 8) / 2}
              r={radius}
              fill="none"
              stroke={isDarkMode ? 'rgba(148,163,184,0.22)' : 'rgba(148,163,184,0.28)'}
              strokeWidth={stroke}
            />
            <circle
              cx={(size + 8) / 2}
              cy={(size + 8) / 2}
              r={radius}
              fill="none"
              stroke={`url(#${gradId})`}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              transform={`rotate(-90 ${(size + 8) / 2} ${(size + 8) / 2})`}
            />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.86rem', fontWeight: 800, color: theme.text }}>
            {progress}%
          </div>
        </div>
        <div style={{ fontSize: '0.55rem', color: theme.textSub, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Complete</div>
      </div>
    );
  };

  // ── Stage card ──
  const StageCard = ({ proj, partNumber, stageObj }) => {
    const row = proj.pipeline[`${stageObj.id}_${partNumber}`] || (stageObj.level === 'interview' ? { status: 'pending', stage: stageObj.id, part_number: 0 } : null);
    if (!row) return null;
    const saveKey    = `${proj.project_root}_${partNumber}_${stageObj.id}`;
    const isSaving   = !!saving[saveKey];
    const isComplete9 = stageObj.id === 9;
    const isAutoStage = stageObj.id <= 3;
    const isOverridden = isAutoStage && row.manual_override;

    // ── Sequential lock: previous stage must be done/skipped ──
    const stagesInLevel = PIPELINE_STAGES_FE.filter(s => s.level === stageObj.level);
    const stageIdx      = stagesInLevel.findIndex(s => s.id === stageObj.id);
    const prevStage     = stageIdx > 0 ? stagesInLevel[stageIdx - 1] : null;
    const prevRow       = prevStage ? proj.pipeline[`${prevStage.id}_${partNumber}`] : null;
    // A missing row for an optional stage = implicitly skipped (not blocking)
    // A missing row for a required stage = not yet created = treat as pending (blocking)
    const prevDone      = !prevStage
      || (prevRow && (prevRow.status === "done" || prevRow.status === "skipped"))
      || (!prevRow && prevStage.optional);

    const isAlreadyDone = row.status === "done" || row.status === "skipped";
    const isLocked = !isAlreadyDone && !prevDone;

    // Visual state
    const isDone    = row.status === "done" || row.status === "skipped";
    const isActive  = row.status === "active";
    const borderCol = isLocked    ? theme.border
      : isOverridden  ? '#f59e0b'
      : isDone        ? (isComplete9 ? '#22c55e' : `${STAGE_COLORS[stageObj.id]?.bg}80`)
      : isActive      ? '#f59e0b'
      : theme.border;

    const cardBg = isLocked
      ? (isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)')
      : theme.sidebar;

    const resetToAuto = () => {
      const key = `${proj.project_root}_${partNumber}_${stageObj.id}`;
      setSaving(s => ({ ...s, [key]: true }));
      axiosAuth.put("/pipeline/stage/reset-auto", {
        project_root: proj.project_root, part_number: partNumber, stage: stageObj.id
      }).then(silentRefresh).catch(() => {})
        .finally(() => setSaving(s => { const n = { ...s }; delete n[key]; return n; }));
    };

    return (
      <div style={{
        background: cardBg,
        border: `1px solid ${borderCol}`,
        borderRadius: '8px', padding: '10px 12px', minWidth: '144px', maxWidth: '176px',
        opacity: isLocked ? 0.45 : 1,
        transition: '0.2s',
        position: 'relative',
      }}>
        {/* Header: number + label */}
        <div style={{ fontSize: '0.67rem', fontWeight: 700, color: isLocked ? theme.textSub : theme.text, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0,
            background: isLocked ? theme.border : (STAGE_COLORS[stageObj.id]?.bg || '#94a3b8'),
            color: '#fff', fontSize: '0.58rem', fontWeight: 800,
          }}>{stageObj.id}</span>
          <span style={{ color: isLocked ? theme.textSub : theme.text }}>{stageObj.label}</span>
          {stageObj.optional && <span style={{ color: '#a78bfa', fontWeight: 400, fontSize: '0.6rem' }}>opt</span>}
          {isLocked && <Icon icon={Lock} size={9} color={theme.textSub} style={{ marginLeft: 'auto' }} />}
          {!isLocked && isAutoStage && !isOverridden && (
            <span title="Auto-detected from disk" style={{ color: '#3b82f6', fontSize: '0.58rem', fontWeight: 400 }}>auto</span>
          )}
          {!isLocked && isOverridden && (
            <span title="Manually overridden" style={{ color: '#f59e0b', fontSize: '0.58rem', fontWeight: 600 }}>manual</span>
          )}
        </div>

        {/* Locked message */}
        {isLocked && prevStage && (
          <div style={{ fontSize: '0.58rem', color: theme.textSub, marginBottom: '4px', fontStyle: 'italic' }}>
            Waiting for Stage {prevStage.id}: {prevStage.label}
          </div>
        )}

        {/* Status buttons */}
        {!isComplete9 && (
          <div style={{ display: 'flex', gap: '3px', marginBottom: '6px', flexWrap: 'wrap' }}>
            {["pending","active","done"].map(st => {
              const meta = PIPELINE_STATUS_META[st];
              const isActiveBtn = row.status === st;
              return (
                <button key={st} disabled={isSaving || isLocked}
                  onClick={() => updateStage(proj.project_root, partNumber, stageObj.id, { status: st })}
                  style={{
                    padding: '2px 6px', borderRadius: '10px', fontSize: '0.61rem',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    border: `1px solid ${isActiveBtn ? meta.color : theme.border}`,
                    background: isActiveBtn ? meta.bg : 'transparent',
                    color: isActiveBtn ? meta.color : theme.textSub,
                    fontWeight: isActiveBtn ? 700 : 400, transition: '0.12s',
                  }}>{meta.label}</button>
              );
            })}
            {isAdmin && stageObj.optional && row.status !== "skipped" && !isLocked && (
              <button onClick={() => skipStage(proj.project_root, partNumber, stageObj.id)}
                style={{ padding: '2px 6px', borderRadius: '10px', border: `1px solid #a78bfa`, background: 'transparent', color: '#a78bfa', fontSize: '0.61rem', cursor: 'pointer' }}>
                skip
              </button>
            )}
            {isAdmin && isOverridden && !isLocked && (
              <button onClick={resetToAuto} disabled={isSaving}
                style={{ padding: '2px 6px', borderRadius: '10px', border: `1px solid #3b82f6`, background: 'transparent', color: '#3b82f6', fontSize: '0.58rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Icon icon={RefreshCw} size={8} /> auto
              </button>
            )}
          </div>
        )}

        {/* Stage 9: complete toggle */}
        {isComplete9 && (
          <button disabled={!isAdmin || isSaving || isLocked}
            onClick={() => updateStage(proj.project_root, 0, 9, { status: row.status === "done" ? "pending" : "done" })}
            style={{
              width: '100%', padding: '5px 0', borderRadius: '6px', marginBottom: '6px',
              border: `1px solid ${row.status === "done" ? '#22c55e' : theme.border}`,
              background: row.status === "done" ? 'rgba(34,197,94,0.15)' : 'transparent',
              color: row.status === "done" ? '#22c55e' : theme.textSub,
              fontWeight: 700, fontSize: '0.7rem',
              cursor: (isAdmin && !isLocked) ? 'pointer' : 'not-allowed',
              opacity: (isAdmin && !isLocked) ? 1 : 0.45,
            }}>
            {row.status === "done" ? "Complete" : "Mark Complete"}
          </button>
        )}

        {/* Assignee */}
        {isAdmin ? (
          <select value={row.assigned_user_id || ""} disabled={isSaving}
            onChange={e => updateStage(proj.project_root, partNumber, stageObj.id, { assigned_user_id: e.target.value ? parseInt(e.target.value) : null })}
            style={inputSm}>
            <option value="">— unassigned —</option>
            {allUsers.filter(u => u.is_active !== false).sort((a, b) => (a.display_name || a.email || '').localeCompare(b.display_name || b.email || '')).map(u => <option key={u.id} value={u.id}>{u.display_name || u.email}</option>)}
          </select>
        ) : (
          row.assigned_user_name
            ? <div style={{ fontSize: '0.61rem', color: theme.textSub, marginTop: '2px' }}>{row.assigned_user_name}</div>
            : null
        )}
      </div>
    );
  };

  // ── Progress dot-trail ──
  const DotTrail = ({ proj }) => (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', width: '100%' }}>
      {PIPELINE_STAGES_FE.map((s, i) => {
        const rows = Object.values(proj.pipeline || {}).filter(r => r.stage === s.id);
        const allDone    = rows.length > 0 && rows.every(r => r.status === "done" || r.status === "skipped");
        const anyActive  = rows.some(r => r.status === "active");
        const allSkipped = rows.length > 0 && rows.every(r => r.status === "skipped");
        const isCurrent  = proj.current_stage === s.id;
        const dotColor   = allDone ? '#22c55e' : allSkipped ? '#a78bfa' : anyActive ? '#f59e0b' : isCurrent ? '#3b82f6' : theme.border;
        const sz = isCurrent ? 8 : 6;
        return (
          <React.Fragment key={s.id}>
            <div title={s.label} style={{
              width: `${sz}px`, height: `${sz}px`, borderRadius: '50%', background: dotColor,
              flexShrink: 0, boxShadow: isCurrent ? `0 0 0 2px ${dotColor}55` : 'none', transition: '0.2s',
            }} />
            {i < PIPELINE_STAGES_FE.length - 1 && (
              <div style={{ flex: 1, height: '1px', background: allDone ? '#22c55e50' : `${theme.border}80`, minWidth: '3px' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const buildTapeRows = (parts) => {
    if (parts.length <= 6) return [parts];
    const split = Math.ceil(parts.length / 2);
    return [parts.slice(0, split), parts.slice(split)];
  };

  const TapeCard = ({ proj, partNum }) => {
    const partRows = Object.values(proj.pipeline || {}).filter(r => r.part_number === partNum && r.part_number > 0);
    const activeStage = (() => {
      for (const s of PIPELINE_STAGES_FE.filter(s => s.level === "part")) {
        const row = partRows.find(r => r.stage === s.id);
        if (!row || row.status === "done" || row.status === "skipped") continue;
        return s;
      }
      return null;
    })();
    const currentStage = activeStage || PIPELINE_STAGES_FE.find(s => s.id === 5);
    const sc       = STAGE_COLORS[currentStage.id] || { bg: '#94a3b8' };
    const isDone   = !activeStage;
    const statusLabel = isDone ? 'Complete' : currentStage.label.replace('& ', '');
    const total    = PIPELINE_STAGES_FE.filter(s => s.level === "part").length;
    const done     = partRows.filter(r => r.status === "done" || r.status === "skipped").length;
    const pct      = total > 0 ? Math.round((done / total) * 100) : 0;
    const col      = isDone ? '#22c55e' : sc.bg;
    return (
      <div title={`Tape ${partNum} — ${isDone ? 'Complete' : currentStage.label} (${pct}%)`} style={{ minWidth: 0, minHeight: '38px', borderRadius: '999px', border: `1px solid ${isDone ? '#22c55e45' : sc.bg + '55'}`, background: isDone ? 'rgba(34,197,94,0.14)' : sc.bg + '18', padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1px', textAlign: 'center', boxShadow: isDone ? 'none' : `inset 0 0 0 1px ${sc.bg}12` }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 800, color: col, letterSpacing: '0.03em', lineHeight: 1, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '3px' }}>
          <Icon icon={Film} size={9} color={col} /> Tape {partNum}
        </div>
        <div style={{ fontSize: '0.53rem', color: col, fontWeight: 700, lineHeight: 1, minHeight: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap' }}>
          {statusLabel}
        </div>
      </div>
    );
  };

  // ── Kanban cell — one stage × one tape ──
  const KanbanCell = ({ proj, partNumber, stageObj }) => {
    const row = proj.pipeline[`${stageObj.id}_${partNumber}`] || (stageObj.level === 'interview' ? { status: 'pending', stage: stageObj.id, part_number: 0 } : null);
    const [hover, setHover] = React.useState(false);
    const [popOpen, setPopOpen] = React.useState(false);
    const cellRef = React.useRef(null);

    React.useEffect(() => {
      if (!popOpen) return;
      const h = (e) => { if (cellRef.current && !cellRef.current.contains(e.target)) setPopOpen(false); };
      document.addEventListener('mousedown', h);
      return () => document.removeEventListener('mousedown', h);
    }, [popOpen]);

    if (!row) {
      // Stage row doesn't exist yet — render empty locked cell
      return (
        <td style={{ padding: '5px 4px', textAlign: 'center', opacity: 0.25 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '22px', borderRadius: '5px', border: `1px dashed ${theme.border}` }}>
            <Icon icon={Lock} size={8} color={theme.textSub} />
          </div>
        </td>
      );
    }

    const saveKey   = `${proj.project_root}_${partNumber}_${stageObj.id}`;
    const isSaving  = !!saving[saveKey];
    const isAutoStage   = stageObj.id <= 3;
    const isOverridden  = isAutoStage && row.manual_override;
    const isComplete9   = stageObj.id === 9;

    // Lock logic (same as StageCard)
    const stagesInLevel = PIPELINE_STAGES_FE.filter(s => s.level === stageObj.level);
    const stageIdx      = stagesInLevel.findIndex(s => s.id === stageObj.id);
    const prevStage     = stageIdx > 0 ? stagesInLevel[stageIdx - 1] : null;
    const prevRow       = prevStage ? proj.pipeline[`${prevStage.id}_${partNumber}`] : null;
    const prevDone      = !prevStage
      || (prevRow && (prevRow.status === 'done' || prevRow.status === 'skipped'))
      || (!prevRow && prevStage.optional);
    const isAlreadyDone = row.status === "done" || row.status === "skipped";
    const isLocked = !isAlreadyDone && !prevDone;

    const statusCycle = { pending: 'active', active: 'done', done: 'pending', skipped: 'pending' };
    const meta = PIPELINE_STATUS_META[row.status] || PIPELINE_STATUS_META.pending;
    const stageCol = STAGE_COLORS[stageObj.id]?.bg || '#94a3b8';

    const cellBg = isLocked
      ? 'transparent'
      : row.status === 'done'    ? (isComplete9 ? 'rgba(34,197,94,0.15)' : `${stageCol}18`)
      : row.status === 'active'  ? 'rgba(245,158,11,0.13)'
      : row.status === 'skipped' ? 'rgba(167,139,250,0.12)'
      : (hover && !isLocked)     ? (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')
      : 'transparent';

    const cellBorder = isLocked
      ? `1px dashed ${theme.border}`
      : row.status === 'done'    ? (isComplete9 ? '1px solid rgba(34,197,94,0.5)' : `1px solid ${stageCol}50`)
      : row.status === 'active'  ? '1px solid rgba(245,158,11,0.5)'
      : row.status === 'skipped' ? '1px solid rgba(167,139,250,0.4)'
      : `1px solid ${hover ? theme.border : 'transparent'}`;

    const handleClick = () => {
      if (isLocked || isSaving) return;
      if (isComplete9) {
        updateStage(proj.project_root, partNumber, stageObj.id, { status: row.status === 'done' ? 'pending' : 'done' });
      } else {
        updateStage(proj.project_root, partNumber, stageObj.id, { status: statusCycle[row.status] || 'active' });
      }
    };

    const assignee = allUsers.find(u => u.id === row.assigned_user_id);
    const assigneeName = assignee ? (assignee.display_name || assignee.email?.split('@')[0]) : null;

    return (
      <td style={{ padding: '4px 3px', textAlign: 'center', position: 'relative' }} ref={cellRef}>
        <div
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => { setHover(false); }}
          onClick={handleClick}
          title={isLocked
            ? (prevStage ? `Waiting for Stage ${prevStage.id}: ${prevStage.label}` : 'Locked')
            : `${stageObj.label}: ${meta.label} — click to advance`}
          style={{
            display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minWidth: '54px', padding: '5px 6px', borderRadius: '7px',
            background: cellBg, border: cellBorder,
            cursor: isLocked ? 'not-allowed' : (isSaving ? 'wait' : 'pointer'),
            opacity: isLocked ? 0.3 : 1,
            transition: 'all 0.12s', gap: '3px', position: 'relative',
          }}>
          {/* Status dot + label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {isSaving
              ? <Icon icon={Loader} size={10} color={meta.color} style={{ animation: 'spin 1s linear infinite' }} />
              : isLocked
              ? <Icon icon={Lock} size={9} color={theme.textSub} />
              : row.status === 'skipped'
              ? <Icon icon={SkipForward} size={10} color="#a78bfa" />
              : row.status === 'done'
              ? <Icon icon={Check} size={10} color={isComplete9 ? '#22c55e' : stageCol} />
              : row.status === 'active'
              ? <Icon icon={Play} size={9} color="#f59e0b" />
              : <div style={{ width: 7, height: 7, borderRadius: '50%', border: `1.5px solid ${theme.border}`, background: 'transparent' }} />
            }
            <span style={{ fontSize: '0.6rem', fontWeight: 700, color: isLocked ? theme.textSub : meta.color, lineHeight: 1 }}>
              {row.status === 'skipped' ? 'skip' : row.status === 'pending' ? '—' : row.status === 'active' ? 'WIP' : 'Done'}
            </span>
          </div>
          {/* Assignee micro-badge */}
          {assigneeName && !isLocked && (
            <span style={{ fontSize: '0.52rem', color: theme.textSub, lineHeight: 1, maxWidth: '52px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {assigneeName.split(' ')[0]}
            </span>
          )}
          {/* Auto/manual badge */}
          {!isLocked && isAutoStage && (
            <span style={{ fontSize: '0.48rem', color: isOverridden ? '#f59e0b' : '#3b82f6', lineHeight: 1, fontWeight: 600 }}>
              {isOverridden ? 'manual' : 'auto'}
            </span>
          )}
          {/* Gear button — opens the assignee / skip / reset popover.
              Rendered on hover for pointer devices, and permanently on touch
              devices, which never fire mouseenter. See CAN_HOVER. */}
          {(hover || !CAN_HOVER) && !isLocked && (
            <button
              onClick={e => { e.stopPropagation(); setPopOpen(o => !o); }}
              aria-label={`Assign, skip or reset ${stageObj.label}`}
              title={`Assign, skip or reset ${stageObj.label}`}
              style={{
                position: 'absolute', top: '2px', right: '2px',
                background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                border: 'none', borderRadius: '3px',
                // Touch gets a larger hit area; the cell is only 54px wide, so
                // this is as close to the 44px guideline as the grid allows.
                padding: CAN_HOVER ? '1px 2px' : '5px 6px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', lineHeight: 1,
              }}>
              <Icon icon={Settings} size={CAN_HOVER ? 8 : 12} style={{ color: theme.textSub }} />
            </button>
          )}
        </div>

        {/* Cell popover — assignee + skip + reset */}
        {popOpen && (
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: Z.dropdown, background: isDarkMode ? '#1a1a2e' : '#fff', border: `1.5px solid ${theme.border}`, borderRadius: '10px', boxShadow: '0 8px 28px rgba(0,0,0,0.22)', padding: '10px 12px', minWidth: '150px', textAlign: 'left' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: theme.text, marginBottom: '8px' }}>
              {stageObj.label}
            </div>
            {/* Status buttons */}
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {['pending','active','done'].map(st => {
                const m = PIPELINE_STATUS_META[st];
                const active = row.status === st;
                return (
                  <button key={st} onClick={() => { updateStage(proj.project_root, partNumber, stageObj.id, { status: st }); setPopOpen(false); }}
                    style={{ padding: '2px 7px', borderRadius: '8px', fontSize: '0.6rem', fontWeight: active ? 700 : 400, border: `1px solid ${active ? m.color : theme.border}`, background: active ? m.bg : 'transparent', color: active ? m.color : theme.textSub, cursor: 'pointer' }}>
                    {m.label}
                  </button>
                );
              })}
              {isAdmin && stageObj.optional && row.status !== 'skipped' && (
                <button onClick={() => { skipStage(proj.project_root, partNumber, stageObj.id); setPopOpen(false); }}
                  style={{ padding: '2px 7px', borderRadius: '8px', fontSize: '0.6rem', border: '1px solid #a78bfa', background: 'transparent', color: '#a78bfa', cursor: 'pointer' }}>
                  Skip
                </button>
              )}
              {isAdmin && isOverridden && (
                <button onClick={() => { const k = `${proj.project_root}_${partNumber}_${stageObj.id}`; setSaving(s => ({...s,[k]:true})); axiosAuth.put('/pipeline/stage/reset-auto',{project_root:proj.project_root,part_number:partNumber,stage:stageObj.id}).then(silentRefresh).catch(()=>{}).finally(()=>setSaving(s=>{const n={...s};delete n[k];return n;})); setPopOpen(false); }}
                  style={{ padding: '2px 7px', borderRadius: '8px', fontSize: '0.6rem', border: '1px solid #3b82f6', background: 'transparent', color: '#3b82f6', cursor: 'pointer', display:'flex', alignItems:'center', gap:'3px' }}>
                  <Icon icon={RefreshCw} size={8} /> Reset auto
                </button>
              )}
            </div>
            {/* Assignee */}
            {isAdmin && (
              <>
                <div style={{ fontSize: '0.58rem', color: theme.textSub, marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assignee</div>
                <Select theme={theme} size="sm" value={row.assigned_user_id || ''} onChange={e => { updateStage(proj.project_root, partNumber, stageObj.id, { assigned_user_id: e.target.value ? parseInt(e.target.value) : null }); }}>
                  <option value="">— unassigned —</option>
                  {allUsers.filter(u => u.is_active !== false).sort((a, b) => (a.display_name || a.email || '').localeCompare(b.display_name || b.email || '')).map(u => <option key={u.id} value={u.id}>{u.display_name || u.email}</option>)}
                </Select>
              </>
            )}
          </div>
        )}
      </td>
    );
  };

  // ── Kanban grid — replaces the old per-tape card stack ──
  const KanbanGrid = ({ proj }) => {
    const partStages      = PIPELINE_STAGES_FE.filter(s => s.level === 'part');
    const interviewStages = PIPELINE_STAGES_FE.filter(s => s.level === 'interview');

    const colW = '72px';
    const labelCol = '68px';

    const thStyle = (stageObj) => ({
      padding: '5px 4px 8px 4px',
      textAlign: 'center',
      width: colW,
      minWidth: colW,
      verticalAlign: 'top',
    });

    return (
      <div style={{ borderTop: `1px solid ${theme.border}`, background: isDarkMode ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.01)', padding: '12px 16px 16px 16px', overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: labelCol, minWidth: labelCol }} />
            {partStages.map(s => <col key={s.id} style={{ width: colW, minWidth: colW }} />)}
          </colgroup>

          {/* Column headers */}
          <thead>
            <tr>
              <th style={{ padding: '0 4px 8px 4px', textAlign: 'left' }}>
                <span style={{ fontSize: '0.55rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-flex', alignItems: 'center', gap: '3px' }}><Icon icon={Film} size={8} color={theme.textSub} /> Tape</span>
              </th>
              {partStages.map(s => {
                const sc = STAGE_COLORS[s.id]?.bg || '#94a3b8';
                return (
                  <th key={s.id} style={thStyle(s)}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: '3px', minHeight: '42px' }}>
                      <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: sc, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '0.6rem', color: '#fff', fontWeight: 800 }}>{s.id}</span>
                      </div>
                      <span style={{ fontSize: '0.58rem', fontWeight: 600, color: theme.textSub, lineHeight: 1.2, textAlign: 'center', maxWidth: '62px' }}>{s.label}</span>
                      {s.optional && <span style={{ fontSize: '0.48rem', color: '#a78bfa', fontWeight: 600 }}>opt</span>}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* One row per tape */}
          <tbody>
            {proj.parts.map((pt, idx) => {
              const b = getPartBucket(proj, pt);
              const bm = DASHBOARD_BUCKETS.find(x => x.key === b);
              const isEven = idx % 2 === 0;
              return (
                <tr key={pt} style={{ background: isEven ? 'transparent' : (isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)') }}>
                  <td style={{ padding: '4px 6px 4px 4px', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Icon icon={Film} size={9} color={theme.textSub} />
                      <span style={{ fontSize: '0.68rem', fontWeight: 600, color: theme.text, whiteSpace: 'nowrap' }}>T{pt}</span>
                      {bm && (
                        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: bm.color, flexShrink: 0 }} title={bm.label} />
                      )}
                    </div>
                  </td>
                  {partStages.map(s => (
                    <KanbanCell key={s.id} proj={proj} partNumber={pt} stageObj={s} />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Interview-level stages */}
        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: `1px dashed ${theme.border}` }}>
          <div style={{ fontSize: '0.55rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>Interview-level</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                {interviewStages.map(s => <col key={s.id} style={{ width: colW, minWidth: colW }} />)}
              </colgroup>
              <thead>
                <tr>
                  {interviewStages.map(s => {
                    const sc = STAGE_COLORS[s.id]?.bg || '#94a3b8';
                    return (
                      <th key={s.id} style={thStyle(s)}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: '3px', minHeight: '42px' }}>
                          <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: sc, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: '0.6rem', color: '#fff', fontWeight: 800 }}>{s.id}</span>
                          </div>
                          <span style={{ fontSize: '0.58rem', fontWeight: 600, color: theme.textSub, lineHeight: 1.2, textAlign: 'center', maxWidth: '62px' }}>{s.label}</span>
                          {s.optional && <span style={{ fontSize: '0.48rem', color: '#a78bfa', fontWeight: 600 }}>opt</span>}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {interviewStages.map(s => (
                    <KanbanCell key={s.id} proj={proj} partNumber={0} stageObj={s} />
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ── Interviewee card — two-line layout with global assignee ──
  const IntervieweeCard = ({ proj, isLast }) => {
    const isExpanded = expandedRoot === proj.project_root;
    const displayName = proj.project_root.replace(/_/g, ' ').replace(/\bpt\d+\b/gi, '').trim();
    const [assignOpen, setAssignOpen] = React.useState(false);
    const assignBtnRef = React.useRef(null);
    const assignDropRef = React.useRef(null);
    const [dropdownPos, setDropdownPos] = React.useState({ top: 0, left: 0, openUp: false });

    React.useEffect(() => {
      if (!assignOpen) return;
      const handler = (e) => {
        if (assignBtnRef.current?.contains(e.target)) return;
        if (assignDropRef.current?.contains(e.target)) return;
        setAssignOpen(false);
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [assignOpen]);

    React.useEffect(() => {
      if (!assignOpen || !assignBtnRef.current) return;
      const rect = assignBtnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 220;
      setDropdownPos({
        top: openUp ? rect.top : rect.bottom + 4,
        left: rect.left,
        openUp,
      });
    }, [assignOpen]);

    const unassignedUsers = allUsers.filter(u => u.is_active !== false && !(proj.assigned_ids || []).includes(u.id));

    const assignOne = (userId) => {
      axiosAuth.put("/pipeline/assign-all", {
        project_root: proj.project_root,
        assigned_user_id: parseInt(userId),
      }).then(silentRefresh).catch(() => {});
      setAssignOpen(false);
    };

    const unassignOne = (userId) => {
      axiosAuth.put("/pipeline/assign-user", {
        project_root: proj.project_root,
        remove_user_id: parseInt(userId),
      }).then(silentRefresh).catch(() => {});
    };

    return (
      <>
        <div
          style={{
            padding: '9px 14px 8px 14px',
            cursor: 'pointer',
            userSelect: 'none',
            background: isExpanded ? (isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)') : 'transparent',
            transition: 'background 0.15s',
            fontSize: theme.fsCardText,
          }}
          onClick={() => setExpandedRoot(isExpanded ? null : proj.project_root)}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 112px', gap: '14px', alignItems: 'start' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', minWidth: 0, justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1, flexWrap: 'wrap' }}>
                <Icon icon={isExpanded ? ChevronDown : ChevronRight} size={12} color={theme.textSub} style={{ flexShrink: 0 }} />
                <button
                  onClick={e => {
                    e.stopPropagation();
                    if (onOpenInterview) onOpenInterview(proj.project_root, proj.file_subdir);
                  }}
                  style={{ background: 'none', border: 'none', padding: 0, margin: 0, fontWeight: 700, fontSize: `calc(${theme.fsCardText} + 2px)`, color: theme.text, cursor: 'pointer', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}
                  title={`Open ${displayName}`}
                >
                  {displayName}
                </button>
                {onSetDefaultProject && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onSetDefaultProject(defaultProject === proj.project_root ? null : proj.project_root);
                    }}
                    title={defaultProject === proj.project_root ? 'Remove favorite' : 'Set as favorite'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                  >
                    <Icon icon={Star} size={13} style={{ color: defaultProject === proj.project_root ? '#facc15' : theme.textSub, fill: defaultProject === proj.project_root ? '#facc15' : 'none', transition: '0.15s', opacity: defaultProject === proj.project_root ? 1 : 0.5 }} />
                  </button>
                )}
                {proj.interview_year && <span style={{ fontSize: '0.65rem', color: theme.textSub, flexShrink: 0 }}>{proj.interview_year}</span>}
                <LangBadge proj={proj} compact />
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                  {(proj.assigned_ids || []).map(uid => {
                    const u = allUsers.find(x => x.id === uid);
                    if (!u) return null;
                    const name = u.display_name || u.email?.split('@')[0];
                    // Name legible on both grounds: light blue on dark, deep blue
                    // on light (the old flat #3b82f6 failed contrast on the dark chip).
                    const chipFg = isDarkMode ? '#bfdbfe' : '#1d4ed8';
                    const chipBtnBg = isDarkMode ? 'rgba(96,165,250,0.28)' : 'rgba(59,130,246,0.18)';
                    return (
                      <span key={uid} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 4px 2px 8px', borderRadius: '12px', background: isDarkMode ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.1)', border: `1px solid ${isDarkMode ? 'rgba(96,165,250,0.4)' : 'rgba(59,130,246,0.3)'}`, fontSize: '0.65rem', fontWeight: 700, color: chipFg, whiteSpace: 'nowrap' }}>
                        <Icon icon={User} size={9} />
                        {name}
                        {isAdmin && <RoleBadge role={u.role} compact />}
                        {isAdmin && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              // Load existing task assignments and open modal
                              setTaskRowsError(false);
                              axiosAuth.get(`/admin/task-assignments/${encodeURIComponent(proj.project_root)}`).then(r => {
                                const userEntry = (r.data || []).find(x => x.user_id === uid);
                                setTaskRows(userEntry ? userEntry.assignments.map(a => ({ ...a })) : []);
                                setTaskRowsError(false);
                              }).catch(() => { setTaskRows([]); setTaskRowsError(true); });
                              setTaskModal({ userId: uid, userName: name, projectRoot: proj.project_root, parts: proj.parts || [] });
                            }}
                            title={`Configure task assignments for ${name}`}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: '50%', border: 'none', background: chipBtnBg, color: chipFg, cursor: 'pointer', padding: 0, flexShrink: 0 }}
                          >
                            <Icon icon={Settings} size={7} />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={e => { e.stopPropagation(); unassignOne(uid); }}
                            title={`Remove ${name}`}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: '50%', border: 'none', background: chipBtnBg, color: chipFg, cursor: 'pointer', padding: 0, flexShrink: 0, fontSize: '0.6rem', fontWeight: 900, lineHeight: 1 }}
                          >
                            <Icon icon={X} size={8} />
                          </button>
                        )}
                      </span>
                    );
                  })}
                  {!isAdmin && proj.assigned_names?.length > 0 && (
                    <span style={{ fontSize: '0.6rem', color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                      <Icon icon={User} size={8} />{proj.assigned_names.join(', ')}
                    </span>
                  )}
                </div>
                </div>
                {isAdmin && (
                  <div style={{ position: 'relative' }}>
                    <button
                      ref={assignBtnRef}
                      onClick={e => { e.stopPropagation(); setAssignOpen(o => !o); }}
                      title="Assign interview"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 10px', borderRadius: '10px', border: `1px dashed ${theme.border}`, background: 'transparent', color: theme.textSub, fontSize: '0.68rem', cursor: 'pointer', fontWeight: 600, height: '30px' }}
                    >
                      <Icon icon={Plus} size={10} />
                      Assign
                    </button>
                    {assignOpen && ReactDOM.createPortal(
                      <div ref={assignDropRef} style={{ position: 'fixed', top: dropdownPos.openUp ? 'auto' : `${dropdownPos.top}px`, bottom: dropdownPos.openUp ? `${window.innerHeight - dropdownPos.top}px` : 'auto', left: `${dropdownPos.left}px`, zIndex: Z.dropdown, background: isDarkMode ? '#1a1a2e' : '#ffffff', border: `1.5px solid ${theme.border}`, borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', minWidth: '160px', overflow: 'hidden', maxHeight: '300px', overflowY: 'auto' }}>
                        {unassignedUsers.length === 0 ? (
                          <div style={{ padding: '10px 12px', fontSize: '0.72rem', color: theme.textSub }}>All users assigned</div>
                        ) : (
                          [...unassignedUsers].sort((a, b) => (a.display_name || a.email || '').localeCompare(b.display_name || b.email || '')).map(u => (
                            <button
                              key={u.id}
                              onClick={e => { e.stopPropagation(); assignOne(u.id); }}
                              style={{ width: '100%', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.78rem', color: theme.text, fontWeight: 500 }}
                              onMouseEnter={e => e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            >
                              <Icon icon={User} size={12} color={theme.textSub} />
                              {u.display_name || u.email}
                              {isAdmin && <RoleBadge role={u.role} compact />}
                            </button>
                          ))
                        )}
                      </div>,
                      document.body
                    )}
                  </div>
                )}
              </div>

              <div style={{ paddingLeft: '20px' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.62rem', fontWeight: 800, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Tapes</span>
                  <div style={{ flex: 1, height: '1px', background: theme.border }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {buildTapeRows(proj.parts).map((row, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`, gap: '8px' }}>
                      {row.map(pt => <TapeCard key={pt} proj={proj} partNum={pt} />)}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ width: '112px', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
              <CompletionDonut pct={proj.progress_pct} width={112} />
            </div>
          </div>
        </div>
        {isExpanded && <KanbanGrid proj={proj} />}
        {!isLast && <div style={{ height: '1px', background: theme.border, margin: '0 14px' }} />}
      </>
    );
  };

  // ── Kanban: bucket projects into columns ──
  const columnBuckets = useMemo(() => {
    const buckets = {};
    kanbanColumns.forEach(col => { buckets[col.key] = []; });
    displayedProjects.forEach(proj => {
      if (proj.progress_pct === 100) {
        buckets[kanbanColumns[kanbanColumns.length - 1].key].push(proj);
      } else {
        const col = getKanbanColumnForStage(kanbanColumns, proj.current_stage);
        buckets[col.key].push(proj);
      }
    });
    return buckets;
  }, [displayedProjects, kanbanColumns]);

  // ── Kanban: move project to a different column ──
  const moveProjectToColumn = async (proj, targetColKey) => {
    const targetCol = kanbanColumns.find(c => c.key === targetColKey);
    if (!targetCol) return;
    const targetFirstStage = Math.min(...targetCol.stages);
    const allParts = (proj.parts || []).slice();
    if (!allParts.length) allParts.push(0);
    const calls = [];
    for (const s of PIPELINE_STAGES_FE) {
      const isPartLevel = s.level === 'part';
      const parts = isPartLevel ? allParts.filter(p => p > 0) : [0];
      for (const pn of parts) {
        if (s.id < targetFirstStage) {
          // Mark prior stages as done (skip optional pending ones)
          const row = Object.values(proj.pipeline || {}).find(r => r.stage === s.id && r.part_number === pn);
          if (row && row.status !== 'done' && row.status !== 'skipped') {
            calls.push(axiosAuth.put("/pipeline/stage", { project_root: proj.project_root, part_number: pn, stage: s.id, status: s.optional && row.status === 'pending' ? 'skipped' : 'done' }));
          }
        } else if (s.id === targetFirstStage) {
          calls.push(axiosAuth.put("/pipeline/stage", { project_root: proj.project_root, part_number: pn, stage: s.id, status: 'active' }));
        }
      }
    }
    if (calls.length) {
      await Promise.all(calls).catch(() => {});
      silentRefresh();
    }
  };

  // ── Kanban project card ──
  const KanbanProjectCard = ({ proj }) => {
    const [expanded, setExpanded] = React.useState(false);
    const displayName = proj.project_root.replace(/_/g, ' ').replace(/\bpt\d+\b/gi, '').trim();
    const currentCol = proj.progress_pct === 100
      ? kanbanColumns[kanbanColumns.length - 1]
      : getKanbanColumnForStage(kanbanColumns, proj.current_stage);
    const tapes = (proj.parts || []).filter(p => p > 0);
    const isComplete = proj.progress_pct === 100;
    const langCode = proj.interview_language ? getLanguageCode(proj.interview_language) : '';
    const langMeta = getLanguageMeta(proj.interview_language);

    // Per-tape status summary
    const tapeStatus = (partNum) => {
      const rows = Object.values(proj.pipeline || {}).filter(r => r.part_number === partNum && r.part_number > 0);
      for (const s of PIPELINE_STAGES_FE.filter(s => s.level === 'part')) {
        const row = rows.find(r => r.stage === s.id);
        if (!row || (row.status !== 'done' && row.status !== 'skipped')) {
          return { stage: s, done: false, pct: rows.length ? Math.round(rows.filter(r => r.status === 'done' || r.status === 'skipped').length / PIPELINE_STAGES_FE.filter(x => x.level === 'part').length * 100) : 0 };
        }
      }
      return { stage: null, done: true, pct: 100 };
    };

    // Mini donut for the card
    const pct = proj.progress_pct || 0;
    const dsz = 32; const dsw = 4; const dr = (dsz - dsw) / 2;
    const dcirc = 2 * Math.PI * dr; const doffset = dcirc * (1 - pct / 100);
    const dcolor = pct >= 100 ? '#22c55e' : pct >= 75 ? '#34d399' : pct >= 50 ? '#4ade80' : pct >= 25 ? '#86efac' : '#bbf7d0';

    return (
      <div
        style={{
          background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '10px',
          overflow: 'hidden', transition: 'box-shadow 0.15s', fontSize: theme.fsCardText,
        }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 0 0 1.5px ${theme.glossaryAccent}45`; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
      >
        {/* Card body — click to open project kanban */}
        <div
          onClick={() => (onOpenProjectKanban || onOpenInterview)(proj.project_root, proj.file_subdir)}
          style={{ padding: '10px 12px', cursor: 'pointer' }}
        >
          {/* Subject name + favorite star + expand chevron */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <div style={{ flex: 1, fontWeight: 700, fontSize: '0.78rem', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </div>
            {onSetDefaultProject && (
              <button
                onClick={e => { e.stopPropagation(); onSetDefaultProject(defaultProject === proj.project_root ? null : proj.project_root); }}
                title={defaultProject === proj.project_root ? 'Remove favorite' : 'Set as favorite'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, display: 'flex', alignItems: 'center' }}
              >
                <Icon icon={Star} size={12} style={{ color: defaultProject === proj.project_root ? '#facc15' : theme.textSub, fill: defaultProject === proj.project_root ? '#facc15' : 'none', transition: '0.15s', opacity: defaultProject === proj.project_root ? 1 : 0.5 }} />
              </button>
            )}
            {/* Registered, media on disk, nothing transcribed yet. Shown so an
                interview cannot be registered and then quietly forgotten, and
                so an empty card reads as "not started" rather than "broken". */}
            {proj.awaiting_transcription && (
              <Badge
                theme={theme}
                status="warning"
                size="sm"
                title="Registered, with media on the archive, but nothing transcribed yet"
                style={{ flexShrink: 0 }}>
                Needs transcribing
              </Badge>
            )}
            {tapes.length > 0 && (
              <button
                onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
                title={expanded ? 'Hide tape details' : 'Show tape details'}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                  color: theme.textSub, display: 'flex', flexShrink: 0, borderRadius: '4px',
                  transition: '0.12s',
                }}
              >
                <Icon icon={expanded ? ChevronUp : ChevronDown} size={12} />
              </button>
            )}
          </div>
          {/* Dot trail (stretches) + mini donut (right) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <div style={{ flex: 1 }}>
              <DotTrail proj={proj} />
            </div>
            <div style={{ position: 'relative', width: dsz, height: dsz, flexShrink: 0 }}>
              <svg width={dsz} height={dsz} viewBox={`0 0 ${dsz} ${dsz}`} style={{ display: 'block' }}>
                <circle cx={dsz/2} cy={dsz/2} r={dr} fill="none" stroke={isDarkMode ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.25)'} strokeWidth={dsw} />
                <circle cx={dsz/2} cy={dsz/2} r={dr} fill="none" stroke={dcolor} strokeWidth={dsw} strokeLinecap="round"
                  strokeDasharray={dcirc} strokeDashoffset={doffset} transform={`rotate(-90 ${dsz/2} ${dsz/2})`}
                  style={{ transition: 'stroke-dashoffset 0.3s, stroke 0.3s' }} />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.48rem', fontWeight: 800, color: pct >= 100 ? '#22c55e' : theme.text }}>
                {pct}
              </div>
            </div>
          </div>
          {/* Language (2-letter) + assignees + tape count */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            {/* While this project's language is being edited, render the shared
                LangBadge so the editor actually appears here. Clicking the chip
                only set langEdit, and nothing in the board view read it — the
                editor lives solely in LangBadge, which board cards never
                rendered — so an admin's click was a visible no-op. */}
            {langCode && isAdmin && langEdit === proj.project_root && (
              <LangBadge proj={proj} compact />
            )}
            {langCode && !(isAdmin && langEdit === proj.project_root) && (
              <span
                onClick={e => { e.stopPropagation(); if (isAdmin) { openLangEdit(proj.project_root, proj.interview_language || ''); } }}
                title={proj.interview_language ? `${proj.interview_language}${proj.language_confirmed ? '' : ' (unconfirmed)'}` : 'Set language'}
                style={{
                  padding: '1px 6px', borderRadius: '999px', fontSize: '0.56rem', fontWeight: 700,
                  background: proj.language_confirmed ? langMeta.bg : 'rgba(245,158,11,0.10)',
                  color: proj.language_confirmed ? langMeta.text : '#f59e0b',
                  border: `1px solid ${proj.language_confirmed ? langMeta.border : 'rgba(245,158,11,0.3)'}`,
                  cursor: isAdmin ? 'pointer' : 'default',
                }}>
                {langCode}
              </span>
            )}
            {(proj.assigned_names || []).map((name, i) => (
              <span key={i} style={{ padding: '1px 6px', borderRadius: '999px', background: `${theme.glossaryAccent}12`, border: `1px solid ${theme.glossaryAccent}30`, fontSize: '0.55rem', fontWeight: 600, color: theme.glossaryAccent, whiteSpace: 'nowrap' }}>
                {name}
              </span>
            ))}
            <span style={{ fontSize: '0.55rem', color: theme.textSub, display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
              <Icon icon={Film} size={8} color={theme.textSub} /> {tapes.length} tape{tapes.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        {/* Expanded tape details */}
        {expanded && tapes.length > 0 && (
          <div style={{ borderTop: `1px solid ${theme.border}`, padding: '8px 12px', background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)' }}>
            {tapes.map(t => {
              const ts = tapeStatus(t);
              const sc = ts.done ? { bg: '#22c55e' } : (STAGE_COLORS[ts.stage?.id] || { bg: '#94a3b8' });
              return (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0' }}>
                  <span style={{ fontSize: '0.6rem', fontWeight: 700, color: sc.bg, minWidth: '50px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <Icon icon={Film} size={8} color={sc.bg} /> Tape {t}
                  </span>
                  <div style={{ flex: 1, height: '4px', borderRadius: '2px', background: `${theme.border}60`, overflow: 'hidden' }}>
                    <div style={{ width: `${ts.pct}%`, height: '100%', borderRadius: '2px', background: sc.bg, transition: '0.3s' }} />
                  </div>
                  <span style={{ fontSize: '0.55rem', fontWeight: 600, color: sc.bg, minWidth: '55px', textAlign: 'right' }}>
                    {ts.done ? 'Complete' : ts.stage?.label?.replace('& ', '') || ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Move dropdown — admin override, hidden when complete */}
        {isAdmin && !isComplete && (
          <div style={{ borderTop: `1px solid ${theme.border}`, padding: '6px 12px', background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)' }}>
            <select
              value={currentCol.key}
              onClick={e => e.stopPropagation()}
              onChange={e => { e.stopPropagation(); moveProjectToColumn(proj, e.target.value); }}
              style={{
                width: '100%', padding: '4px 6px', borderRadius: '6px',
                border: `1px solid ${theme.border}`, background: theme.inputBg,
                color: theme.text, fontSize: '0.62rem', cursor: 'pointer',
              }}
            >
              {kanbanColumns.map(col => (
                <option key={col.key} value={col.key}>Move to: {col.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    );
  };

  // ── Column configuration panel ──
  const ColumnConfigPanel = () => {
    const allStageIds = PIPELINE_STAGES_FE.map(s => s.id);
    const assignedStages = new Set(kanbanColumns.flatMap(c => c.stages));

    const updateColumn = (idx, patch) => {
      setKanbanColumns(cols => cols.map((c, i) => i === idx ? { ...c, ...patch } : c));
    };
    const removeColumn = (idx) => {
      setKanbanColumns(cols => {
        const removed = cols[idx];
        const next = cols.filter((_, i) => i !== idx);
        // Move orphaned stages to the previous column (or first)
        if (removed.stages.length && next.length) {
          const target = Math.min(idx, next.length - 1);
          next[target] = { ...next[target], stages: [...next[target].stages, ...removed.stages].sort((a, b) => a - b) };
        }
        return next;
      });
    };
    const addColumn = () => {
      const key = `col_${Date.now()}`;
      setKanbanColumns(cols => [...cols, { key, label: 'New Column', stages: [] }]);
    };
    const toggleStage = (colIdx, stageId) => {
      setKanbanColumns(cols => {
        const next = cols.map((c, i) => {
          if (i === colIdx) {
            return c.stages.includes(stageId)
              ? { ...c, stages: c.stages.filter(s => s !== stageId) }
              : { ...c, stages: [...c.stages, stageId].sort((a, b) => a - b) };
          }
          // Remove from other columns
          return { ...c, stages: c.stages.filter(s => s !== stageId) };
        });
        return next;
      });
    };

    return (
      <div style={{ background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Configure Columns</span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={() => setKanbanColumns(DEFAULT_KANBAN_COLUMNS)} style={{ padding: '3px 9px', borderRadius: '6px', fontSize: '0.58rem', fontWeight: 600, border: `1px solid ${theme.border}`, background: theme.card, color: theme.textSub, cursor: 'pointer' }}>
              Reset Default
            </button>
            <button onClick={addColumn} style={{ padding: '3px 9px', borderRadius: '6px', fontSize: '0.58rem', fontWeight: 600, border: `1px solid ${theme.glossaryAccent}`, background: `${theme.glossaryAccent}12`, color: theme.glossaryAccent, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <Icon icon={Plus} size={9} /> Add Column
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
          {kanbanColumns.map((col, colIdx) => (
            <div key={col.key} style={{ flex: '1 0 150px', minWidth: '150px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '8px', padding: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                <Input
                  theme={theme}
                  size="sm"
                  fullWidth={false}
                  value={col.label}
                  onChange={e => updateColumn(colIdx, { label: e.target.value })}
                  style={{ flex: 1, fontWeight: 700 }}
                />
                {kanbanColumns.length > 1 && (
                  <button onClick={() => removeColumn(colIdx)} title="Remove column" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px', display: 'flex' }}>
                    <Icon icon={Trash2} size={10} />
                  </button>
                )}
              </div>
              {allStageIds.map(sid => {
                const stage = PIPELINE_STAGES_FE.find(s => s.id === sid);
                const inThisCol = col.stages.includes(sid);
                const inOther = !inThisCol && assignedStages.has(sid);
                const sc = STAGE_COLORS[sid];
                return (
                  <label key={sid} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '2px 0', fontSize: '0.6rem', cursor: 'pointer', opacity: inOther && !inThisCol ? 0.4 : 1 }}>
                    <input type="checkbox" checked={inThisCol} onChange={() => toggleStage(colIdx, sid)} style={{ accentColor: sc.bg }} />
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: sc.bg, flexShrink: 0 }} />
                    <span style={{ color: theme.text }}>{stage.label}</span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
        {/* Warn about unassigned stages */}
        {allStageIds.some(id => !assignedStages.has(id)) && (
          <div style={{ marginTop: '8px', fontSize: '0.58rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Icon icon={AlertTriangle} size={10} />
            Some stages are not assigned to any column
          </div>
        )}
      </div>
    );
  };

  // ── Kanban board view ──
  const KanbanBoardView = () => (
    <div>
      {showColumnConfig && isAdmin && <ColumnConfigPanel />}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', paddingBottom: '8px' }}>
        {kanbanColumns.map(col => {
          const cards = columnBuckets[col.key] || [];
          const firstStageColor = STAGE_COLORS[col.stages[0]]?.bg || '#94a3b8';
          return (
            <div key={col.key} style={{ flex: '1 1 0', minWidth: 0 }}>
              {/* Column header */}
              <div style={{
                borderTop: `3px solid ${firstStageColor}`, borderRadius: '8px 8px 0 0',
                background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
                padding: '10px 12px 8px', marginBottom: '6px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.68rem', color: theme.text }}>{col.label}</span>
                  <span style={{
                    padding: '1px 7px', borderRadius: '999px', fontSize: '0.58rem', fontWeight: 700,
                    background: `${firstStageColor}18`, color: firstStageColor, border: `1px solid ${firstStageColor}40`,
                  }}>
                    {cards.length}
                  </span>
                </div>
                {/* Stage color pills */}
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {col.stages.map(sid => {
                    const stage = PIPELINE_STAGES_FE.find(s => s.id === sid);
                    const sc = STAGE_COLORS[sid]?.bg || '#94a3b8';
                    return (
                      <span key={sid} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 6px 1px 4px', borderRadius: '4px', background: `${sc}18`, border: `1px solid ${sc}40`, fontSize: '0.5rem', fontWeight: 700 }}>
                        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: sc, flexShrink: 0 }} />
                        <span style={{ color: sc }}>{stage?.label || sid}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
              {/* Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: '60px' }}>
                {cards.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '20px 8px', color: theme.textSub, fontSize: '0.62rem', fontStyle: 'italic' }}>
                    No interviews
                  </div>
                )}
                {cards.map(proj => <KanbanProjectCard key={proj.project_root} proj={proj} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ══════════════════════════════════════════
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px', background: theme.bg }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700, color: theme.text, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Dashboard</h2>
          <div style={{ fontSize: '0.82rem', color: theme.textSub, marginTop: '3px' }}>
            Welcome back, {welcomeName}
          </div>
        </div>

        {/* Working Directory */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', background: theme.sidebar, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '12px 16px' }}>
          <Icon icon={FolderOpen} size={16} color={theme.glossaryAccent} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.58rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Working Directory</div>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {workingDirLabel || 'Not set'}
            </div>
          </div>
          <Button
            theme={theme}
            variant="primary"
            size="sm"
            icon={FolderOpen}
            onClick={onOpenDirectory}
          >
            Change
          </Button>
        </div>

        {/* Team Workload (admin only) — compact cards (name + role + count
            badges), sortable, with the active/pending/done breakdown shown as a
            detail only on the selected member. Clicking a member filters the
            interview list below to their assignments. */}
        {isAdmin && usersSummary.length > 0 && (() => {
          const roleRank = { admin: 0, editor: 1, reviewer: 2, viewer: 3 };
          const roleOf = (id) => allUsers.find(x => x.id === id)?.role || 'viewer';
          const projOf = (id) => projects.filter(p => (p.assigned_ids || []).includes(id)).length;
          const tasksOf = (u) => (u.active_count || 0) + (u.pending_count || 0) + (u.done_count || 0);
          const nameOf = (u) => (u.display_name || u.email || '');
          const sorted = [...usersSummary].sort((a, b) => {
            if (workloadSort === 'access') { const d = roleRank[roleOf(a.id)] - roleRank[roleOf(b.id)]; if (d) return d; }
            else if (workloadSort === 'projects') { const d = projOf(b.id) - projOf(a.id); if (d) return d; }
            else if (workloadSort === 'tasks') { const d = tasksOf(b) - tasksOf(a); if (d) return d; }
            return nameOf(a).localeCompare(nameOf(b));
          });
          const SORTS = [
            { key: 'name', label: 'Name' },
            { key: 'access', label: 'Access' },
            { key: 'projects', label: 'Projects' },
            { key: 'tasks', label: 'Tasks' },
          ];
          return (
          <div style={{ background: theme.sidebar, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 700, fontSize: '0.72rem', color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Team Workload</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.58rem', color: theme.textSub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sort</span>
                {SORTS.map(s => {
                  const on = workloadSort === s.key;
                  return (
                    <button key={s.key} onClick={() => setWorkloadSort(s.key)}
                      style={{ padding: '3px 10px', borderRadius: '999px', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer', border: `1px solid ${on ? theme.glossaryAccent : theme.border}`, background: on ? theme.glossaryAccent : 'transparent', color: on ? contrastText(theme.glossaryAccent) : theme.textSub }}>
                      {s.label}
                    </button>
                  );
                })}
                {userFilterId != null && (
                  <button onClick={() => setUserFilterId(null)}
                    style={{ padding: '3px 9px', borderRadius: '999px', fontSize: '0.62rem', cursor: 'pointer', border: `1px solid ${theme.glossaryAccent}`, background: `${theme.glossaryAccent}15`, color: theme.glossaryAccent, fontWeight: 700 }}>
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {sorted.map(u => {
                const assignedProjects = projOf(u.id);
                const totalTasks = tasksOf(u);
                const active = userFilterId === u.id;
                return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => { setUserFilterId(prev => prev === u.id ? null : u.id); setSortKey('name'); setSortDir('asc'); }}
                  title={`${nameOf(u)} — ${u.active_count} active, ${u.pending_count} pending, ${u.done_count} done`}
                  style={{ background: theme.card, borderRadius: '10px', padding: '9px 12px', border: `1px solid ${active ? theme.glossaryAccent : theme.border}`, minWidth: '150px', textAlign: 'left', cursor: 'pointer', fontSize: theme.fsCardText }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, marginBottom: '6px' }}>
                    <div style={{ fontWeight: 700, fontSize: theme.fsCardText, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nameOf(u)}
                    </div>
                    <RoleBadge role={roleOf(u.id)} compact />
                  </div>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    <span style={{ padding: '2px 7px', borderRadius: '999px', background: active ? `${theme.glossaryAccent}18` : theme.bg, border: `1px solid ${active ? `${theme.glossaryAccent}55` : theme.border}`, color: active ? theme.glossaryAccent : theme.textSub, fontSize: '0.6rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {assignedProjects} proj{assignedProjects === 1 ? '' : 's'}
                    </span>
                    <span style={{ padding: '2px 7px', borderRadius: '999px', background: theme.bg, border: `1px solid ${theme.border}`, color: theme.textSub, fontSize: '0.6rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {totalTasks} task{totalTasks === 1 ? '' : 's'}
                    </span>
                  </div>
                  {active && (
                    <div style={{ display: 'flex', gap: '8px', fontSize: '0.62rem', marginTop: '7px', paddingTop: '6px', borderTop: `1px solid ${theme.border}` }}>
                      <span style={{ color: theme.warning || '#f59e0b' }}>{u.active_count} active</span>
                      <span style={{ color: theme.textSub }}>{u.pending_count} pending</span>
                      <span style={{ color: theme.success || '#22c55e' }}>{u.done_count} done</span>
                    </div>
                  )}
                </button>
              )})}
            </div>
          </div>
          );
        })()}

        {/* Empty state — no projects loaded (and not still loading) */}
        {!loading && projects.length === 0 && (
          <div style={{ background: theme.sidebar, border: `1px dashed ${theme.border}`, borderRadius: '12px', padding: '36px 28px', textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', width: '56px', height: '56px', borderRadius: '999px', background: theme.card, border: `1px solid ${theme.border}`, alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>
              <Icon icon={isAdmin ? FolderOpen : User} size={22} color={theme.textSub} />
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: theme.text, marginBottom: '6px', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              {isAdmin ? 'No interview projects found' : 'No assignments yet'}
            </div>
            <div style={{ fontSize: '0.78rem', color: theme.textSub, maxWidth: '440px', margin: '0 auto 18px', lineHeight: 1.55 }}>
              {isAdmin
                ? 'The current working directory has no VTT files. Point the app at a directory that contains interview transcripts to get started.'
                : 'You have not been assigned to any interviews yet. Reach out to an admin to request access to a project.'}
            </div>
            {isAdmin && (
              <Button
                theme={theme}
                variant="primary"
                icon={FolderOpen}
                onClick={onOpenDirectory}
              >
                Change Working Directory
              </Button>
            )}
          </div>
        )}

        {/* Interviews card */}
        {!(projects.length === 0 && !loading) && (
        <div style={{ background: theme.sidebar, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '20px' }}>

          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
            <div style={{ fontWeight: 700, fontSize: '0.72rem', color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {userFilterId != null
                ? `${(allUsers.find(u => u.id === userFilterId)?.display_name || allUsers.find(u => u.id === userFilterId)?.email || 'Selected user').replace(/\s+/g, ' ').trim()} Assignments`
                : (isAdmin ? 'All Interviews' : 'Your Assignments')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {/* List / Board toggle */}
              <div style={{ display: 'flex', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '999px', padding: '2px', gap: '2px' }}>
                {[
                  { key: 'list',  label: 'List',  Ic: List },
                  { key: 'board', label: 'Board', Ic: LayoutGrid },
                ].map(({ key, label, Ic }) => {
                  const isActive = viewMode === key;
                  return (
                    <button key={key} onClick={() => setViewMode(key)} style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '4px 12px', borderRadius: '999px', border: 'none',
                      background: isActive ? `${theme.glossaryAccent}20` : 'transparent',
                      color: isActive ? theme.glossaryAccent : theme.textSub,
                      fontWeight: isActive ? 700 : 500, fontSize: '0.72rem', cursor: 'pointer', transition: '0.12s',
                      outline: isActive ? `1.5px solid ${theme.glossaryAccent}45` : 'none',
                    }}>
                      {Ic && <Icon icon={Ic} size={11} />}
                      {label}
                    </button>
                  );
                })}
              </div>
              <PageHelp title="Dashboard" theme={theme} steps={[
                'Click a subject card to open their workspace.',
                'Use the star icon to favorite subjects — they appear first.',
                'Toggle between List and Board view with the buttons above.',
                'Board view groups subjects by pipeline stage — admins can use the Move to menu on a card to change its column.',
              ]} />
              {/* Column config gear — board mode, admin only */}
              {viewMode === 'board' && isAdmin && (
                <button
                  onClick={() => setShowColumnConfig(v => !v)}
                  title="Configure board columns"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '28px', height: '28px', borderRadius: '999px', border: `1px solid ${showColumnConfig ? theme.glossaryAccent : theme.border}`,
                    background: showColumnConfig ? `${theme.glossaryAccent}15` : theme.card,
                    color: showColumnConfig ? theme.glossaryAccent : theme.textSub,
                    cursor: 'pointer', transition: '0.12s',
                  }}
                >
                  <Icon icon={Settings} size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Assignment filter toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>Assignment</span>
            <div style={{ display: 'flex', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '999px', padding: '2px', gap: '2px' }}>
              {[
                { key: 'assigned',   label: 'Assigned',   Ic: UserCheck },
                { key: 'unassigned', label: 'Unassigned', Ic: User },
              ].map(({ key, label, Ic }) => {
                const isActive = assignFilter === key;
                const accentColor = key === 'assigned' ? '#22c55e' : '#f59e0b';
                return (
                  <button key={key} onClick={() => setAssignFilter(key)} style={{
                    display: 'flex', alignItems: 'center', gap: '5px',
                    padding: '4px 12px', borderRadius: '999px', border: 'none',
                    background: isActive ? `${accentColor}20` : 'transparent',
                    color: isActive ? accentColor : theme.textSub,
                    fontWeight: isActive ? 700 : 500, fontSize: '0.72rem', cursor: 'pointer', transition: '0.12s',
                    outline: isActive ? `1.5px solid ${accentColor}45` : 'none',
                  }}>
                    {Ic && <Icon icon={Ic} size={11} />}
                    {label}
                    <span style={{
                      background: isActive ? accentColor : theme.border,
                      color: isActive ? '#fff' : theme.textSub,
                      borderRadius: '8px', padding: '0 6px', fontSize: '0.6rem', fontWeight: 700, marginLeft: '1px',
                    }}>{assignCounts[key]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sort bar */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '14px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              flexWrap: 'wrap',
              width: '100%',
              padding: '8px 12px',
              borderRadius: '12px',
              border: `1px solid ${theme.border}`,
              background: isDarkMode ? 'rgba(255,255,255,0.055)' : 'rgba(0,0,0,0.04)',
            }}>
              <span style={{ fontSize: '0.62rem', color: theme.textSub, marginRight: '4px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sort</span>
              {SORT_OPTIONS.map(opt => {
                const active = sortKey === opt.key;
                return (
                  <button key={opt.key} onClick={() => toggleSort(opt.key)} style={{
                    padding: '4px 10px', borderRadius: '999px', fontSize: '0.62rem', cursor: 'pointer',
                    border: `1px solid ${active ? theme.glossaryAccent : theme.border}`,
                    background: active ? `${theme.glossaryAccent}15` : theme.card,
                    color: active ? theme.glossaryAccent : theme.textSub,
                    fontWeight: active ? 700 : 500,
                    display: 'flex', alignItems: 'center', gap: '3px',
                  }}>
                    {opt.label}
                    {active && <Icon icon={sortDir === 'asc' ? ChevronUp : ChevronDown} size={10} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Interview list / Kanban board */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: theme.textSub, fontSize: '0.85rem' }}>Loading pipeline…</div>
          ) : displayedProjects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: theme.textSub, fontSize: '0.85rem' }}>No interviews match this filter.</div>
          ) : viewMode === 'board' ? (
            <KanbanBoardView />
          ) : (
            <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', background: theme.card }}>
              {/* Stage colour legend */}
              <div style={{ padding: '8px 14px', background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)', borderBottom: `1px solid ${theme.border}`, display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', minWidth: 0 }}>
                <span style={{ fontSize: '0.55rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: '4px', flexShrink: 0 }}>Stages</span>
                {PIPELINE_STAGES_FE.map(s => {
                  const sc = STAGE_COLORS[s.id];
                  return (
                    <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '1px 7px 1px 5px', borderRadius: '4px', background: `${sc.bg}22`, border: `1px solid ${sc.bg}55`, fontSize: '0.56rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: sc.bg, flexShrink: 0 }} />
                      <span style={{ color: sc.bg }}>{s.label}</span>
                    </span>
                  );
                })}
                </div>
                {isAdmin && (
                  <button
                    onClick={runRecalculate}
                    disabled={recalcState === 'running'}
                    title="Re-scan all VTT files and transcript marks to update pipeline stage statuses"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '4px 10px', borderRadius: '8px', fontSize: '0.62rem',
                      fontWeight: 600, cursor: recalcState === 'running' ? 'wait' : 'pointer',
                      border: `1px solid ${recalcState?.updated_rows > 0 ? theme.success : theme.border}`,
                      background: recalcState?.updated_rows > 0 ? `${theme.success}15` : theme.card,
                      color: recalcState?.error ? theme.accent : recalcState?.updated_rows >= 0 ? theme.success : theme.textSub,
                      opacity: recalcState === 'running' ? 0.6 : 1,
                      transition: '0.2s',
                      marginLeft: '12px',
                      flexShrink: 0,
                    }}
                  >
                    <Icon icon={RefreshCw} size={10} style={{ animation: recalcState === 'running' ? 'spin 1s linear infinite' : 'none' }} />
                    {recalcState === 'running'
                      ? 'Recalculating…'
                      : recalcState?.error
                        ? 'Error — retry'
                        : recalcState?.updated_rows >= 0
                          ? `${recalcState.updated_rows} updated`
                          : 'Recalculate'}
                  </button>
                )}
              </div>
              {displayedProjects.map((proj, idx) => (
                <IntervieweeCard key={proj.project_root} proj={proj} isLast={idx === displayedProjects.length - 1} />
              ))}
            </div>
          )}
        </div>
        )}

        {/* Needs Assignment (admin) */}
        {isAdmin && (() => {
          const unassigned = projects.filter(proj =>
            !proj.language_confirmed
          );
          if (!unassigned.length) return null;
          return (
            <div style={{ background: theme.sidebar, border: `1.5px dashed ${theme.border}`, borderRadius: '12px', padding: '18px 20px', marginTop: '20px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.72rem', color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                <Icon icon={AlertTriangle} size={13} color="#f59e0b" />
                Language Unconfirmed ({unassigned.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {unassigned.map(proj => (
                  <div key={proj.project_root} style={{ background: theme.card, borderRadius: '8px', padding: '10px 14px', border: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', gap: '12px', fontSize: theme.fsCardText }}>
                    <div style={{ flex: 1, fontWeight: 600, fontSize: theme.fsCardText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {proj.project_root.replace(/_/g, ' ')}
                    </div>
                    <LangBadge proj={proj} />
                    <Button theme={theme} variant="primary" size="sm"
                      onClick={() => { setExpandedRoot(proj.project_root); }}
                      style={{ flexShrink: 0 }}>
                      Review
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

      </div>

      {/* ── Task Assignment Modal ── */}
      {taskModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: Z.modal, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setTaskModal(null)}>
          <div style={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '14px', padding: '24px 28px', maxWidth: '520px', width: '92%', boxShadow: '0 16px 48px rgba(0,0,0,0.4)', maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '0.95rem', color: theme.text }}>Task Assignments</h3>
                <div style={{ fontSize: '0.72rem', color: theme.textSub, marginTop: '2px' }}>
                  <strong>{taskModal.userName}</strong> on <strong>{taskModal.projectRoot.replace(/_/g, ' ')}</strong>
                </div>
              </div>
              <button onClick={() => setTaskModal(null)} style={{ background: 'none', border: 'none', color: theme.textSub, cursor: 'pointer', padding: '4px' }}>
                <Icon icon={X} size={16} />
              </button>
            </div>

            {taskRowsError ? (
              <LoadFailure theme={theme} what="this user's task assignments" compact />
            ) : taskRows.length === 0 && (
              <div style={{ padding: '16px 0', textAlign: 'center', fontSize: '0.78rem', color: theme.textSub }}>
                Full access to all tapes (no restrictions)
              </div>
            )}

            {taskRows.map((row, idx) => {
              const fmtTC = (s) => {
                if (s === null || s === undefined || s === '') return '';
                const n = parseFloat(s);
                if (isNaN(n)) return '';
                const h = Math.floor(n / 3600);
                const m = Math.floor((n % 3600) / 60);
                const sec = Math.floor(n % 60);
                return h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}` : `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
              };
              const parseTC = (str) => {
                if (!str || !str.trim()) return null;
                const parts = str.trim().split(':').map(Number);
                if (parts.some(isNaN)) return null;
                if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
                if (parts.length === 2) return parts[0] * 60 + parts[1];
                return parts[0];
              };
              const updateRow = (key, val) => setTaskRows(prev => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));
              return (
                <div key={row.id || `new-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: `1px solid ${theme.border}30` }}>
                  <select
                    value={row.part_number ?? ''}
                    onChange={e => updateRow('part_number', e.target.value === '' ? null : parseInt(e.target.value))}
                    style={{ width: '80px', padding: '5px 6px', borderRadius: '6px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '0.72rem' }}
                  >
                    <option value="">All</option>
                    {(taskModal.parts || []).map(p => <option key={p} value={p}>Tape {p}</option>)}
                  </select>
                  <input
                    type="text"
                    placeholder="Start"
                    value={row._tcStartStr !== undefined ? row._tcStartStr : fmtTC(row.tc_start)}
                    onChange={e => updateRow('_tcStartStr', e.target.value)}
                    onBlur={e => { const v = parseTC(e.target.value); updateRow('tc_start', v); updateRow('_tcStartStr', undefined); }}
                    style={{ width: '62px', padding: '5px 6px', borderRadius: '6px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '0.68rem', fontFamily: 'monospace', textAlign: 'center' }}
                    title="Start timecode (MM:SS or HH:MM:SS)"
                  />
                  <span style={{ color: theme.textSub, fontSize: '0.7rem' }}>–</span>
                  <input
                    type="text"
                    placeholder="End"
                    value={row._tcEndStr !== undefined ? row._tcEndStr : fmtTC(row.tc_end)}
                    onChange={e => updateRow('_tcEndStr', e.target.value)}
                    onBlur={e => { const v = parseTC(e.target.value); updateRow('tc_end', v); updateRow('_tcEndStr', undefined); }}
                    style={{ width: '62px', padding: '5px 6px', borderRadius: '6px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '0.68rem', fontFamily: 'monospace', textAlign: 'center' }}
                    title="End timecode (MM:SS or HH:MM:SS)"
                  />
                  <input
                    type="text"
                    placeholder="Note (e.g. Yiddish speaker)"
                    value={row.note || ''}
                    onChange={e => updateRow('note', e.target.value || null)}
                    style={{ flex: 1, padding: '5px 6px', borderRadius: '6px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '0.68rem', minWidth: '80px' }}
                  />
                  <button
                    onClick={() => setTaskRows(prev => prev.filter((_, i) => i !== idx))}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px', flexShrink: 0 }}
                    title="Remove this assignment"
                  >
                    <Icon icon={Trash2} size={12} />
                  </button>
                </div>
              );
            })}

            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button
                onClick={() => setTaskRows(prev => [...prev, { id: null, part_number: null, tc_start: null, tc_end: null, note: null }])}
                style={{ padding: '6px 12px', borderRadius: '8px', border: `1px dashed ${theme.border}`, background: 'transparent', color: theme.textSub, fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Icon icon={Plus} size={10} /> Add Range
              </button>
              <div style={{ flex: 1 }} />
              <Button
                theme={theme}
                variant="outline"
                size="sm"
                onClick={() => setTaskModal(null)}
              >
                Cancel
              </Button>
              <Button
                theme={theme}
                variant="accent"
                size="sm"
                disabled={taskSaving}
                onClick={() => {
                  setTaskSaving(true);
                  const { userId, projectRoot } = taskModal;
                  // Delete all existing, then create new ones
                  axiosAuth.get(`/admin/task-assignments/${encodeURIComponent(projectRoot)}`).then(r => {
                    const existing = (r.data || []).find(x => x.user_id === userId);
                    const deleteIds = (existing?.assignments || []).map(a => a.id);
                    return Promise.all(deleteIds.map(id => axiosAuth.delete(`/admin/task-assignments/${id}`)));
                  }).then(() => {
                    // Create all new assignments
                    return Promise.all(taskRows.map(row =>
                      axiosAuth.post('/admin/task-assignments', {
                        user_id: userId,
                        project_root: projectRoot,
                        part_number: row.part_number,
                        tc_start: row.tc_start,
                        tc_end: row.tc_end,
                        note: row.note || null,
                      })
                    ));
                  }).then(() => {
                    setTaskModal(null);
                    setTaskSaving(false);
                  }).catch(() => setTaskSaving(false));
                }}
              >
                {taskSaving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
